import { http } from '@/core/api'
import { resolveExcelHeavyBlobExport } from '@/shared/utils/excel-heavy-operation'

export type FollowUpHistoryDashboard = {
  total: number
  with_next_follow: number
  overdue_next_follow: number
  contacted: number
  period_from: string
  period_to: string
}

export type FollowUpHistoryRow = {
  id: number
  created_at: string | null
  contact_status: string
  care_status?: string | null
  expected_job_change_on?: string | null
  expected_job_search_on?: string | null
  result_status: string
  note: string | null
  not_looking_reason?: string | null
  next_follow_at: string | null
  /** Counts toward the 30-day hold: real contact or recorded progress. */
  counts_as_valid?: boolean
  valid_basis?: string | null
  assignment_id: number | null
  worker_id: number | null
  channel: 'zalo' | 'direct'
  zalo_application_id: number | null
  zalo_conversation_id: number | null
  user: { id: number; name: string } | null
  worker: {
    id: number
    worker_code: string | null
    full_name: string | null
    phone_masked: string | null
    identity_number_display?: string | null
  } | null
  lead: FollowUpHistoryLead | null
  team: { id: number; name: string } | null
}

export type FollowUpHistoryLead = {
  lead_code: string | null
  full_name: string | null
  phone_masked: string | null
  lead_created_at: string | null
  /** When the lead was distributed onto this follow-up's assignment. */
  assigned_at: string | null
  platform_label: string | null
  traffic_channel_label: string | null
  traffic_source_label: string | null
  channel_label: string | null
  ingestion_method_label: string | null
  gateway_entry_label: string | null
  external_id: string | null
  import_batch_code: string | null
  worker_code: string | null
  interested_client_label: string | null
  display_status: string | null
  duplicate_of_lead_code: string | null
  assignee_name: string | null
  imported_by_name: string | null
  note: string | null
}

/** One cared lead with nested follow-up lines in the filtered period. */
export type FollowUpHistoryLeadGroup = {
  worker_id: number | null
  follow_up_count: number
  last_followed_at: string | null
  latest: FollowUpHistoryRow
  follow_ups: FollowUpHistoryRow[]
}

export type FollowUpHistorySummary = {
  total: number
  by_contact: Array<{ key: string; count: number; percent: number }>
  by_result: Array<{ key: string; count: number; percent: number }>
}

export type FollowUpHistoryFilterOptions = {
  contact_statuses: string[]
  result_statuses: string[]
  staff: Array<{ id: number; name: string; employee_status?: string | null }>
  can_filter_staff: boolean
  only_mine_default: boolean
}

export type FollowUpHistoryListParams = {
  page?: number
  per_page?: number
  created_from?: string
  created_to?: string
  search?: string
  contact_status?: string
  result_status?: string
  platform?: string
  traffic_channel_id?: number
  traffic_source_id?: number
  user_id?: number
  only_mine?: boolean
  overdue_next_follow?: boolean
  /** Paginate by lead (worker); each item nests follow_ups. */
  group_by?: 'lead'
}

type PaginatedMeta = {
  current_page: number
  last_page: number
  per_page: number
  total: number
  grouped_by?: string
}

export const followUpHistoryApi = {
  async dashboard(params?: { created_from?: string; created_to?: string }) {
    const { data } = await http.get('/recruitment/follow-ups/history/dashboard', { params })
    return data as { data: FollowUpHistoryDashboard }
  },

  async list(params: FollowUpHistoryListParams) {
    const { data } = await http.get('/recruitment/follow-ups/history', { params })
    return data as { data: FollowUpHistoryLeadGroup[] | FollowUpHistoryRow[]; meta: PaginatedMeta }
  },

  async listGrouped(params: Omit<FollowUpHistoryListParams, 'group_by'>) {
    const { data } = await http.get('/recruitment/follow-ups/history', {
      params: { ...params, group_by: 'lead' },
    })
    return data as { data: FollowUpHistoryLeadGroup[]; meta: PaginatedMeta }
  },

  async summary(params: Omit<FollowUpHistoryListParams, 'page' | 'per_page' | 'group_by'>) {
    const { data } = await http.get('/recruitment/follow-ups/history/summary', { params })
    return data as { data: FollowUpHistorySummary }
  },

  async filterOptions(_force = false) {
    const { data } = await http.get('/recruitment/follow-ups/history/filter-options')
    return data as { data: FollowUpHistoryFilterOptions }
  },

  async export(params: Record<string, string | number | boolean | undefined>) {
    const { page: _page, per_page: _perPage, group_by: _groupBy, ...filters } = params
    const res = await http.get<Blob>('/recruitment/follow-ups/history/export', {
      params: filters,
      responseType: 'blob',
      timeout: 300_000,
      validateStatus: (status) => status === 200 || status === 202,
    })
    return resolveExcelHeavyBlobExport(res, { timeoutMs: 300_000 })
  },
}
